import { useRef, useEffect } from "react";
import { ReactLenis } from "lenis/react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

import { Header } from "@/components/site/Header";
import { WaterLevel } from "@/components/site/WaterLevel";
import { StickyCTA } from "@/components/site/StickyCTA";
import { Hero } from "@/components/site/Hero";
import { Marquee } from "@/components/site/Marquee";
import { FirstHours } from "@/components/site/FirstHours";
import { ClockTimeline } from "@/components/site/ClockTimeline";
import { MythBusters } from "@/components/site/MythBusters";
import { WaitingTrap } from "@/components/site/WaitingTrap";
import { WhoseSide } from "@/components/site/WhoseSide";
import { WhatHappens } from "@/components/site/WhatHappens";
import { TrustStrip } from "@/components/site/TrustStrip";
import { IntakeForm } from "@/components/site/IntakeForm";
import { ContextCTA } from "@/components/site/ContextCTA";
import { Footer } from "@/components/site/Footer";

function Page() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll();

  // The water recedes: deep submerged -> mid -> shallow -> dry neutral.
  const bg = useTransform(
    scrollYProgress,
    [0, 0.28, 0.52, 0.62, 1],
    ["#0A1E2E", "#12496B", "#3D8DB0", "#EDE7DC", "#F2EEE6"]
  );

  return (
    <div ref={ref} className="relative">
      <motion.div className="fixed inset-0 -z-50" style={{ backgroundColor: bg }} />

      <Header />
      <WaterLevel />
      <StickyCTA />

      <main>
        <Hero />
        <Marquee />
        <FirstHours />
        <ContextCTA
          testid="cta-after-timeline"
          tone="dark"
          label="Begin your intake"
          micro="You understand step one — let's start it."
        />
        <ClockTimeline />
        <ContextCTA
          testid="cta-after-clock"
          tone="dark"
          label="Get a crew moving now"
          micro="The sooner we start, the smaller the job stays."
        />
        <MythBusters />
        <WaitingTrap />
        <ContextCTA
          testid="cta-after-trap"
          tone="light"
          label="Don't wait — start your intake"
          micro="Prompt, documented action protects your home and your claim."
        />
        <WhoseSide />
        <ContextCTA
          testid="cta-after-side"
          tone="light"
          label="Put a crew on your side"
          micro="Someone whose only job is your home. Start now."
        />
        <WhatHappens />
        <TrustStrip />
        <IntakeForm />
      </main>

      <Footer />
    </div>
  );
}

function App() {
  const reduce = useReducedMotion();
  const lenisRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      if (lenisRef.current?.lenis) {
        window.__lenis = lenisRef.current.lenis;
        clearInterval(id);
      }
    }, 100);
    return () => {
      clearInterval(id);
      delete window.__lenis;
    };
  }, []);

  if (reduce) return <Page />;

  return (
    <ReactLenis root ref={lenisRef} options={{ lerp: 0.09, duration: 1.2, smoothWheel: true }}>
      <Page />
    </ReactLenis>
  );
}

export default App;
